/**
 * Step 3 of the welcome: add a few first clients by hand or from a CSV file. Each person goes through the
 * normal "add a person" route: ONE server transaction makes the contact (dedupe-checked), the company and one
 * pipeline entry per ticked business area, all owned by the user. Nothing is written here directly.
 */
import { errorMessage } from '../api/client.js';
import { tp } from '../modules/pipeline/tpClient.js';
import { areasFromCell } from '../components/ui/area-multi-select.js';

/** A client can belong to several businesses (owner request 2026-09-21). */
export interface ClientRow { name: string; company: string; phone: string; areaIds: string[] }
export interface AddResult { row: ClientRow; ok: boolean; message: string }

export const MAX_QUICK = 5;
export const MAX_CSV = 200;

/** Indian mobile numbers are often typed as 10 digits or with spaces; the server needs +91XXXXXXXXXX. */
export function normalisePhone(raw: string): string | null {
  const t = raw.replace(/[\s\-().]/g, '');
  if (!t) return null;
  if (/^\+[1-9]\d{6,14}$/.test(t)) return t;
  if (/^0?[6-9]\d{9}$/.test(t)) return `+91${t.slice(-10)}`;
  if (/^91[6-9]\d{9}$/.test(t)) return `+${t}`;
  return null;
}

/** Minimal CSV reader (quoted fields, commas inside quotes, CRLF). */
export function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = []; let cell = ''; let q = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (q) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') q = false;
      else cell += ch;
    } else if (ch === '"') q = true;
    else if (ch === ',') { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell); cell = '';
      if (row.some((c) => c.trim())) rows.push(row);
      row = [];
    } else cell += ch;
  }
  row.push(cell);
  if (row.some((c) => c.trim())) rows.push(row);
  return rows;
}

/**
 * CSV -> client rows. Columns by header name: name, company, phone, areas (or area). The areas cell may name
 * several businesses separated by ; or | ("EnrollUp; SEO"), each matched to the area name or key; with none
 * the default areas are used. Returns plain-English problems for rows that cannot be used.
 */
export function rowsFromCsv(text: string, areas: { verticalId: string; key: string; name: string }[], defaultAreaIds: string[]): { rows: ClientRow[]; problems: string[] } {
  const grid = parseCsvText(text);
  const problems: string[] = [];
  if (grid.length === 0) return { rows: [], problems: ['The file is empty.'] };
  const head = grid[0]!.map((h) => h.trim().toLowerCase());
  const col = (n: string) => head.indexOf(n);
  if (col('name') < 0) return { rows: [], problems: ['The first row must have the column names, including "name".'] };
  const rows: ClientRow[] = [];
  grid.slice(1).forEach((r, i) => {
    const line = i + 2;
    const get = (n: string) => (col(n) >= 0 ? (r[col(n)] ?? '').trim() : '');
    const name = get('name');
    if (!name) { problems.push(`Row ${line}: no name, left out.`); return; }
    const cell = col('areas') >= 0 ? get('areas') : get('area');
    const { ids, unknown } = areasFromCell(cell, areas.map((a) => ({ value: a.verticalId, label: a.name, key: a.key })));
    if (unknown.length) problems.push(`Row ${line}: business area ${unknown.map((u) => `"${u}"`).join(', ')} not found, ${ids.length ? 'left out' : 'used the ticked ones'}.`);
    rows.push({ name, company: get('company'), phone: get('phone'), areaIds: ids.length ? ids : [...defaultAreaIds] });
  });
  if (rows.length > MAX_CSV) {
    problems.push(`Only the first ${MAX_CSV} people are added here. Use Reach > Import for bigger files.`);
    rows.length = MAX_CSV;
  }
  return { rows, problems };
}

/**
 * Add one person: one contact + company + one "New: not contacted yet" entry per ticked area, in one server
 * transaction. Areas where the person already is are skipped, and the result says so plainly.
 */
export async function addClient(row: ClientRow, ownerUserId: string): Promise<AddResult> {
  const name = row.name.trim();
  if (!name) return { row, ok: false, message: 'No name.' };
  if (row.areaIds.length === 0) return { row, ok: false, message: 'Tick at least one business.' };
  const phone = row.phone.trim() ? normalisePhone(row.phone) : null;
  if (row.phone.trim() && !phone) return { row, ok: false, message: `"${row.phone}" does not look like a phone number.` };
  try {
    const r = await tp('addPerson', {
      body: { verticalIds: row.areaIds, name, company: row.company.trim() || name, ...(phone ? { phone } : {}), section: 'O', ownerUserId },
    });
    if (r.duplicates.length) return { row, ok: false, message: 'Already in your list, so not added again.' };
    const skipped = r.skippedAreas.map((x) => x.reason).join(' ');
    if (!r.relationship) return { row, ok: false, message: skipped || 'Not added.' };
    const n = r.relationships.length;
    return { row, ok: true, message: `${n > 1 ? `Added to ${n} businesses.` : 'Added.'}${skipped ? ` ${skipped}` : ''}` };
  } catch (e) {
    console.error('quick add failed', e);
    return { row, ok: false, message: errorMessage(e) };
  }
}
